import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getProxy, getSite, registerProxy, updateProxy } from "./proxyService.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Collapse,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Alert,
  Badge,
  Spinner,
} from "reactstrap";
// core components
import HusariaHeader from "views/pages/components/HusariaHeader.js";
import { serverHost } from "variables/serverdata.js";

function ProxyForm() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const location = useLocation();

  const labelStyle = {
    color: isDark ? "#ffffff" : "#000000",
  };

  const sectionHeadingStyle = {
    color: isDark ? "#f5f0f0" : "#181212",
  };

  const gpfOptions = ["GPF1", "GPF2", "GPF3", "GPF4", "GPF5"];
  const optionalSaltoFieldOptions = [
    { value: "HUSARIAONE", label: "HusariaOne" },
    ...gpfOptions.map((value) => ({ value, label: value })),
  ];

  const [saltoShipOpen, setSaltoShipOpen] = useState(false);
  const [showSaltoShipPassword, setShowSaltoShipPassword] = useState(false);

  const normalizeProxyResponse = (data, site_uuid) => ({
    proxy_name: data.proxy_name || "",
    proxy_ip: data.proxy_ip || "",
    proxy_uuid: data.proxy_uuid || "",
    proxy_version: data.proxy_version || "",
    mqtt_topic: data.mqtt_topic || "",
    enable: data.enable || "y",
    site_id: data.site_uuid || site_uuid,
    last_checkin: data.last_checkin || null,
    created_at: data.created_at || null,
    latest_version: data.latest_version || "",
    update_available: data.update_available || false,

    salto_sync_enabled: Boolean(data.salto_sync_enabled),
    salto_ship_url: data.salto_ship_url || "",
    salto_ship_username: data.salto_ship_username || "",
    // The backend intentionally never returns the stored password.
    salto_ship_password: "",
    salto_ship_password_set: Boolean(data.salto_ship_password_set),
    salto_vnumber_gpf: data.salto_vnumber_gpf || "GPF1",
    salto_phone1_gpf: data.salto_phone1_gpf || "GPF2",
    salto_phone2_gpf: data.salto_phone2_gpf || "HUSARIAONE",
    salto_phone3_gpf: data.salto_phone3_gpf || "HUSARIAONE",
    salto_pin_gpf: data.salto_pin_gpf || "HUSARIAONE",
  });

  // Remove proxyId usage, always use site_id from current user
  const [proxyData, setProxyData] = useState({
    proxy_name: '',
    proxy_ip: '',
    proxy_uuid: '',
    proxy_version: '',
    mqtt_topic: '',
    enable: '',
    site_id: '',
    last_checkin: null,
    created_at: null,
    latest_version: "",
    update_available: false,

    salto_sync_enabled: false,
    salto_ship_url: "",
    salto_ship_username: "",
    salto_ship_password: "",
    salto_ship_password_set: false,
    salto_vnumber_gpf: "GPF1",
    salto_phone1_gpf: "GPF2",
    salto_phone2_gpf: "HUSARIAONE",
    salto_phone3_gpf: "HUSARIAONE",
    salto_pin_gpf: "HUSARIAONE",
  });

  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState({ show: false, success: false, message: '' });
  const [savedProxy, setSavedProxy] = useState(null);
  const [updateSending, setUpdateSending] = useState(false);
  const [proxyUpdating, setProxyUpdating] = useState(false);

  const statusProxy = savedProxy;
  const lastCheckin = statusProxy?.last_checkin
    ? new Date(statusProxy.last_checkin)
    : null;

  const isOnline =
    lastCheckin &&
    Date.now() - lastCheckin.getTime() <= 5 * 60 * 1000;

  // Listen for site changes
  useEffect(() => {
    const handleSiteChange = (event) => {
      console.log("Site changed to:", event.detail);
      loadProxyData(event.detail);
    };

    window.addEventListener('siteChanged', handleSiteChange);
    return () => {
      window.removeEventListener('siteChanged', handleSiteChange);
    };
  }, []);

  // Load proxy data based on site_uuid
  const loadProxyData = async (site_uuid) => {
    setLoading(true);
    console.log("Loading proxy data for site:", site_uuid);

    try {
      const data = await getProxy(site_uuid);

      if (data) {
        const normalized = normalizeProxyResponse(data, site_uuid);

        setProxyData(normalized);
        setSavedProxy(normalized);

      } else {
        const defaults = {
          proxy_name: "",
          proxy_ip: "",
          proxy_version: "",
          mqtt_topic: "",
          enable: "",
          site_id: site_uuid,
          last_checkin: null,
          created_at: null,
          proxy_uuid: null,
          latest_version: "",
          update_available: false,
          salto_sync_enabled: false,
          salto_ship_url: "",
          salto_ship_username: "",
          salto_ship_password: "",
          salto_ship_password_set: false,
          salto_vnumber_gpf: "GPF1",
          salto_phone1_gpf: "GPF2",
          salto_phone2_gpf: "HUSARIAONE",
          salto_phone3_gpf: "HUSARIAONE",
          salto_pin_gpf: "HUSARIAONE",
        };

        setProxyData(defaults);
        setSavedProxy(null);
      }

    } catch (err) {
      console.error("Error loading proxy:", err);
      setSaveStatus({
        show: true,
        success: false,
        message: "Error loading proxy data: " + err.message
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { site_uuid } = getSite();
    if (site_uuid) loadProxyData(site_uuid);
  }, []);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    const field = id.replace('input-', '');

    if (type === 'checkbox') {
      setProxyData(prev => ({
        ...prev,
        [field]: field === "enable" ? (checked ? "y" : "n") : checked,
      }));
      return;
    }

    setProxyData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const waitForProxyVersionRefresh = async (site_uuid, expectedVersion) => {
    for (let i = 0; i < 10; i++) {
      const data = await getProxy(site_uuid);

      if (!expectedVersion || data.proxy_version === expectedVersion || data.update_available === false) {
        const normalized = normalizeProxyResponse(data, site_uuid);

        setProxyData(normalized);
        setSavedProxy(normalized);
        return true;
      }

      await new Promise(resolve => setTimeout(resolve, 3000));
    }

    return false;
  };

  const handleUpdateProxy = async () => {
    if (proxyUpdating) return;

    setProxyUpdating(true);
    setSaveStatus({
      show: true,
      success: true,
      message: "Updating proxy. Please wait...",
    });

    try {
      const { site_uuid } = getSite();
      if (!site_uuid) throw new Error("No site selected");

      const res = await fetch(
        `${serverHost}/hrms/proxy/update/${site_uuid}`,
        { method: "POST" }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || data.error || "Proxy update failed");
      }

      setSaveStatus({
        show: true,
        success: true,
        message: data.message || "Proxy updated successfully.",
      });

      // refresh proxy so version + update_available update immediately
      const refreshed = await waitForProxyVersionRefresh(
        site_uuid,
        data.target_version || proxyData.latest_version
      );

      if (!refreshed) {
        setProxyData(prev => ({
          ...prev,
          proxy_version: data.target_version || prev.latest_version || prev.proxy_version,
          update_available: false,
        }));

        setSavedProxy(prev => prev
          ? {
            ...prev,
            proxy_version: data.target_version || prev.proxy_version,
            update_available: false,
          }
          : prev
        );
      }

    } catch (err) {
      setSaveStatus({
        show: true,
        success: false,
        message: err.message || "Proxy update failed",
      });
    } finally {
      setProxyUpdating(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      if (!proxyData.proxy_name || !proxyData.mqtt_topic) {
        setSaveStatus({
          show: true,
          success: false,
          message: "Please fill in all required fields",
        });
        return;
      }

      const { site_uuid } = getSite();
      if (!site_uuid) {
        setSaveStatus({
          show: true,
          success: false,
          message: "No site selected",
        });
        return;
      }

      if (proxyData.salto_sync_enabled) {
        const hasPassword =
          proxyData.salto_ship_password_set ||
          Boolean(proxyData.salto_ship_password?.trim());

        if (
          !proxyData.salto_ship_url?.trim() ||
          !proxyData.salto_ship_username?.trim() ||
          !hasPassword
        ) {
          setSaveStatus({
            show: true,
            success: false,
            message: "Please enter the Salto SHIP URL, username, and password.",
          });
          return;
        }

        if (!/^https?:\/\//i.test(proxyData.salto_ship_url.trim())) {
          setSaveStatus({
            show: true,
            success: false,
            message: "Salto SHIP URL must start with http:// or https://.",
          });
          return;
        }
      }

      const saltoSettings = {
        salto_sync_enabled: proxyData.salto_sync_enabled,
        salto_ship_url: proxyData.salto_ship_url?.trim() || "",
        salto_ship_username: proxyData.salto_ship_username?.trim() || "",
        salto_vnumber_gpf: proxyData.salto_vnumber_gpf || "GPF1",
        salto_phone1_gpf: proxyData.salto_phone1_gpf || "GPF2",
        salto_phone2_gpf: proxyData.salto_phone2_gpf || "HUSARIAONE",
        salto_phone3_gpf: proxyData.salto_phone3_gpf || "HUSARIAONE",
        salto_pin_gpf: proxyData.salto_pin_gpf || "HUSARIAONE",
      };

      // A blank password means "keep the password already stored".
      if (proxyData.salto_ship_password) {
        saltoSettings.salto_ship_password = proxyData.salto_ship_password;
      }

      let proxy;
      const shouldApplyOnsiteSaltoConfig =
        proxyData.salto_sync_enabled || Boolean(savedProxy?.salto_sync_enabled);

      if (savedProxy?.proxy_uuid) {
        proxy = await updateProxy(savedProxy.proxy_uuid, {
          proxy_name: proxyData.proxy_name,
          proxy_version: proxyData.proxy_version,
          mqtt_topic: proxyData.mqtt_topic,
          enable: proxyData.enable,
          ...saltoSettings,
        });
      } else {
        proxy = await registerProxy(site_uuid, {
          proxy_name: proxyData.proxy_name,
          proxy_version: proxyData.proxy_version,
          mqtt_topic: proxyData.mqtt_topic,
          enable: proxyData.enable,
          ...saltoSettings,
        });
      }

      let onsiteConfigError = null;

      // The database save above is the source of truth. This second request
      // copies those saved values into the onsite proxy's local config.py.
      if (shouldApplyOnsiteSaltoConfig) {
        try {
          const configResponse = await fetch(
            `${serverHost}/hrms/proxy/salto-config/${site_uuid}`,
            { method: "POST" }
          );

          const configResult = await configResponse.json().catch(() => ({}));

          if (!configResponse.ok || configResult.success === false) {
            throw new Error(
              configResult.error ||
              configResult.message ||
              "The onsite proxy did not accept the Salto configuration."
            );
          }
        } catch (configError) {
          onsiteConfigError = configError.message;
        }
      }

      setSaveStatus({
        show: true,
        success: !onsiteConfigError,
        message: onsiteConfigError
          ? (
            "The settings were saved in HusariaOne, but the onsite config.py " +
            `was not updated: ${onsiteConfigError} ` +
            "Update the proxy, wait for it to come back online, then click Save again."
          )
          : shouldApplyOnsiteSaltoConfig
            ? "Proxy and onsite Salto configuration saved successfully!"
            : "Proxy saved successfully!",
      });

      const normalized = normalizeProxyResponse(proxy, site_uuid);

      setSavedProxy(normalized);
      setProxyData(normalized);
    } catch (err) {
      console.error("Error saving proxy:", err);
      setSaveStatus({
        show: true,
        success: false,
        message: "Error saving proxy: " + err.message,
      });
    }
  };

  if (loading) {
    return (
      <>
        <HusariaHeader name="Proxy Configuration" parentName="HRMS" padding="4.5rem" />
        <Container className="mt--6" fluid>
          <Row>
            <Col xl="12">
              <Card>
                <CardBody className="py-5 text-center">
                  <Spinner />
                  <div className="mt-3 text-muted">Loading proxy configuration...</div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <HusariaHeader name="Proxy Configuration" parentName="HRMS" padding="4.5rem" />
      <Container className="mt--6" fluid>
        {saveStatus.show && (
          <Alert
            color={saveStatus.success ? "success" : "danger"}
            isOpen={saveStatus.show}
            toggle={() => setSaveStatus({ ...saveStatus, show: false })}
          >
            {saveStatus.message}
          </Alert>
        )}
        {proxyUpdating && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Card style={{ width: "420px", textAlign: "center" }}>
              <CardBody className="py-5">
                <Spinner color="info" />
                <h3 className="mt-4 mb-2">Updating Proxy</h3>
                <p className="text-muted mb-0">
                  The proxy is downloading and applying the update. Please wait.
                </p>
              </CardBody>
            </Card>
          </div>
        )}
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card>
              <CardHeader
                className="border-0 header-theme" style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}>
                <Row className="align-items-center">
                  <Col xs="8">
                  </Col>
                  <Col xs="4" className="d-flex justify-content-end">
                    {proxyData.update_available && (
                      <Button
                        color="success"
                        size="sm"
                        className="mr-2"
                        onClick={handleUpdateProxy}
                        disabled={proxyUpdating}
                      >
                        {proxyUpdating ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Updating...
                          </>
                        ) : (
                          "Update Proxy"
                        )}
                      </Button>
                    )}
                    <Button
                      color="info"
                      onClick={handleSave}
                      size="sm"
                    >
                      Save
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form>
                  <h6 className="heading-small mb-4" style={sectionHeadingStyle}>
                    Proxy information
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-proxy_name"
                            style={labelStyle}
                          >
                            Proxy Name <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-proxy_name"
                            value={proxyData.proxy_name}
                            onChange={handleInputChange}
                            placeholder="Enter proxy name"
                            type="text"
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-mqtt_topic"
                            style={labelStyle}
                          >
                            MQTT Topic <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-mqtt_topic"
                            value={proxyData.mqtt_topic}
                            onChange={handleInputChange}
                            placeholder="Enter MQTT topic"
                            type="text"
                          //disabled
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-proxy_version"
                            style={labelStyle}
                          >
                            Version
                          </label>
                          <div className="d-flex align-items-center">
                            <Input
                              id="input-proxy_version"
                              value={proxyData.proxy_version || "Unknown"}
                              readOnly
                            />

                            {proxyData.update_available && (
                              <Badge
                                color="warning"
                                className="ml-2"
                                style={{
                                  height: "28px",
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "0 10px",
                                  fontWeight: 600,
                                }}
                              >
                                Update Available
                              </Badge>
                            )}
                          </div>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-proxy_ip"
                            style={labelStyle}
                          >
                            IP Address
                          </label>
                          <Input
                            id="input-proxy_ip"
                            value={proxyData.proxy_ip}
                            onChange={handleInputChange}
                            placeholder="Enter IP address"
                            type="text"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-proxy_uuid"
                            style={labelStyle}
                          >
                            Proxy UUID
                          </label>
                          <Input
                            id="input-proxy_uuid"
                            value={proxyData.proxy_uuid}
                            onChange={handleInputChange}
                            placeholder="Enter proxy UUID"
                            type="text"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-site_id"
                            style={labelStyle}
                          >
                            Site UUID
                          </label>
                          <Input
                            id="input-site_id"
                            value={proxyData.site_id}
                            onChange={handleInputChange}
                            placeholder="Enter site UUID"
                            type="text"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-enable"
                            style={labelStyle}
                          >
                            Enable
                          </label>
                          <div>
                            <label className="custom-toggle">
                              <input
                                type="checkbox"
                                id="input-enable"
                                checked={proxyData.enable === 'y'}
                                onChange={handleInputChange}
                              />
                              <span
                                className="custom-toggle-slider rounded-circle"
                                data-label-off="No"
                                data-label-on="Yes"
                              />
                            </label>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-status"
                            style={labelStyle}
                          >
                            Status
                          </label>
                          <div className="d-flex align-items-center">
                            <div style={{ marginRight: "10px" }}>
                              {savedProxy?.last_checkin ? (
                                <Badge
                                  color={
                                    Date.now() - new Date(savedProxy.last_checkin).getTime() <= 5 * 60 * 1000
                                      ? "success"
                                      : "danger"
                                  }
                                >
                                  {Date.now() - new Date(savedProxy.last_checkin).getTime() <= 5 * 60 * 1000
                                    ? "Online"
                                    : "Offline"}
                                </Badge>
                              ) : (
                                <Badge color="warning" style={{ color: "#ff3709" }}>
                                  Never Connected
                                </Badge>
                              )}
                            </div>

                            {savedProxy?.last_checkin && (
                              <div className="form-control-label" style={labelStyle}>
                                Last check-in: {new Date(savedProxy.last_checkin).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                  <hr className="my-4" />

                  <h6 className="heading-small mb-4" style={sectionHeadingStyle}>
                    Salto SHIP Sync
                  </h6>

                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-salto_sync_enabled"
                            style={labelStyle}
                          >
                            Enable Salto SHIP Sync
                          </label>

                          <div>
                            <label className="custom-toggle">
                              <input
                                type="checkbox"
                                id="input-salto_sync_enabled"
                                checked={Boolean(proxyData.salto_sync_enabled)}
                                onChange={handleInputChange}
                              />
                              <span
                                className="custom-toggle-slider rounded-circle"
                                data-label-off="No"
                                data-label-on="Yes"
                              />
                            </label>
                          </div>

                          <small className="form-text text-muted">
                            Enable tenant syncing through the SHIP server configured in Salto Space.
                          </small>
                        </FormGroup>
                      </Col>

                      <Col lg="6" className="d-flex align-items-start justify-content-lg-end">
                        <Button
                          color="secondary"
                          size="sm"
                          type="button"
                          onClick={() => setSaltoShipOpen(prev => !prev)}
                          style={{
                            marginTop: "1.95rem",
                            backgroundColor: isDark ? "#374151" : undefined,
                            borderColor: isDark ? "#4b5563" : undefined,
                            color: isDark ? "#ffffff" : undefined,
                          }}
                        >
                          {saltoShipOpen ? "Hide GPF Fields" : "Configure GPF Fields"}
                        </Button>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-salto_ship_url"
                            style={labelStyle}
                          >
                            Salto SHIP Server URL
                          </label>

                          <Input
                            id="input-salto_ship_url"
                            type="url"
                            value={proxyData.salto_ship_url || ""}
                            onChange={handleInputChange}
                            disabled={!proxyData.salto_sync_enabled}
                            placeholder="Enter Salto Space URL"
                            required={proxyData.salto_sync_enabled}
                          />

                          <small className="form-text text-muted">
                            HTTP(S) endpoint for this site's Salto SHIP service.
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-salto_ship_username"
                            style={labelStyle}
                          >
                            Salto SHIP Username
                          </label>

                          <Input
                            id="input-salto_ship_username"
                            type="text"
                            value={proxyData.salto_ship_username || ""}
                            onChange={handleInputChange}
                            disabled={!proxyData.salto_sync_enabled}
                            autoComplete="off"
                            required={proxyData.salto_sync_enabled}
                            placeholder="Enter Salto SHIP username"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-salto_ship_password"
                            style={labelStyle}
                          >
                            Salto SHIP Password
                          </label>
                          <div style={{ position: "relative" }}>
                            <Input
                              id="input-salto_ship_password"
                              type={showSaltoShipPassword ? "text" : "password"}
                              value={proxyData.salto_ship_password || ""}
                              onChange={handleInputChange}
                              disabled={!proxyData.salto_sync_enabled}
                              autoComplete="new-password"
                              placeholder={
                                proxyData.salto_ship_password_set
                                  ? "Password already saved"
                                  : "Enter the SHIP Basic Auth password"
                              }
                              required={
                                proxyData.salto_sync_enabled &&
                                !proxyData.salto_ship_password_set
                              }
                              style={{ paddingRight: "2.75rem" }}
                            />

                            <button
                              type="button"
                              onClick={() =>
                                setShowSaltoShipPassword((currentlyShown) => !currentlyShown)
                              }
                              disabled={
                                !proxyData.salto_sync_enabled ||
                                !proxyData.salto_ship_password
                              }
                              aria-label={
                                showSaltoShipPassword ? "Hide password" : "Show password"
                              }
                              title={
                                showSaltoShipPassword ? "Hide password" : "Show password"
                              }
                              style={{
                                position: "absolute",
                                top: "50%",
                                right: "0.85rem",
                                transform: "translateY(-50%)",
                                padding: 0,
                                border: "none",
                                background: "transparent",
                                color: "#8898aa",
                                cursor:
                                  proxyData.salto_sync_enabled &&
                                    proxyData.salto_ship_password
                                    ? "pointer"
                                    : "default",
                                opacity:
                                  proxyData.salto_sync_enabled &&
                                    proxyData.salto_ship_password
                                    ? 1
                                    : 0.4,
                                lineHeight: 1,
                                zIndex: 2,
                              }}
                            >
                              <i
                                className={
                                  showSaltoShipPassword ? "fas fa-eye-slash" : "fas fa-eye"
                                }
                                aria-hidden="true"
                              />
                            </button>
                          </div>

                          <small className="form-text text-muted">
                            {proxyData.salto_ship_password_set
                              ? "Leave blank to keep the saved password. Enter a value to replace it."
                              : "Enter the password used by the Salto SHIP service."}
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Collapse isOpen={saltoShipOpen} style={{ width: "100%" }}>
                      <div style={{ width: "100%" }}>
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-salto_vnumber_gpf"
                                style={labelStyle}
                              >
                                Virtual Number Field
                              </label>

                              <Input
                                id="input-salto_vnumber_gpf"
                                type="select"
                                value={proxyData.salto_vnumber_gpf || "GPF1"}
                                onChange={handleInputChange}
                                disabled={!proxyData.salto_sync_enabled}
                              >
                                {gpfOptions.map(option => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Input>

                              <small className="form-text text-muted">
                                Salto GPF field used for tenant virtual number.
                              </small>
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-salto_phone1_gpf"
                                style={labelStyle}
                              >
                                Phone Number Field
                              </label>

                              <Input
                                id="input-salto_phone1_gpf"
                                type="select"
                                value={proxyData.salto_phone1_gpf || "GPF2"}
                                onChange={handleInputChange}
                                disabled={!proxyData.salto_sync_enabled}
                              >
                                {gpfOptions.map(option => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Input>

                              <small className="form-text text-muted">
                                Salto GPF field used for Phone 1.
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          {[
                            {
                              key: "salto_phone2_gpf",
                              label: "Phone Number 2",
                              help: "Choose a Salto GPF field, or HusariaOne to keep Phone 2 editable.",
                            },
                            {
                              key: "salto_phone3_gpf",
                              label: "Phone Number 3",
                              help: "Choose a Salto GPF field, or HusariaOne to keep Phone 3 editable.",
                            },
                            {
                              key: "salto_pin_gpf",
                              label: "PIN",
                              help: "Choose a Salto GPF field, or HusariaOne to keep the PIN editable.",
                            },
                          ].map((field) => (
                            <Col lg="4" key={field.key}>
                              <FormGroup>
                                <label
                                  className="form-control-label"
                                  htmlFor={`input-${field.key}`}
                                  style={labelStyle}
                                >
                                  {field.label}
                                </label>

                                <Input
                                  id={`input-${field.key}`}
                                  type="select"
                                  value={proxyData[field.key]}
                                  onChange={handleInputChange}
                                  disabled={!proxyData.salto_sync_enabled}
                                >
                                  {optionalSaltoFieldOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Input>

                                <small className="form-text text-muted">
                                  {field.help}
                                </small>
                              </FormGroup>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </Collapse>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ProxyForm;